import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { isActiveJobStatus, isPublishedJobStatus } from '@/lib/jobStatusSemantics';

const HIDDEN_CV_SCORING_STATUSES = new Set([
  'cv_scoring_pending',
  'cv_scoring_failed',
  'duplicate_review_pending',
  'rejected_unreadable',
]);

export function useDashboardStats() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['dashboard', 'stats', orgId],
    queryFn: async () => {
    const [jobsRes, applicationsRes, interviewsRes, interviewConfigsRes] = await Promise.all([
        supabase.from('jobs').select('id, status, closes_at, created_at').eq('org_id', orgId!),
        supabase.from('applications').select('id, stage, job_id, created_at, updated_at, status, cv_scores(total_score)'),
        supabase.from('interview_sessions').select('id, status, completed_at'),
        supabase.from('interview_configs').select('id, job_id, status, questions_json'),
      ]);

      const jobs = jobsRes.data || [];
      const rawApplications = applicationsRes.data || [];
      const interviews = interviewsRes.data || [];
      const interviewConfigs = interviewConfigsRes.data || [];

      // Count published interview configs whose job is still active and have at least one question
      const activeJobIdSet = new Set(jobs.filter(j => isActiveJobStatus(j.status)).map(j => j.id));
      const activeInterviewConfigs = interviewConfigs.filter((c) =>
        c.status === 'published' && Array.isArray(c.questions_json) && c.questions_json.length > 0 && activeJobIdSet.has(c.job_id)
      ).length;

      // Match Candidates page filter: exclude unreadable and unscored applied-stage
      const applications = rawApplications.filter((a) => {
        if (HIDDEN_CV_SCORING_STATUSES.has(a.status)) return false;
        const score = a.cv_scores?.[0]?.total_score ?? a.cv_scores?.total_score;
        if (a.stage === 'applied' && score == null) return false;
        return true;
      });

      const totalJobsCreated = jobs.filter(j => isPublishedJobStatus(j.status)).length;
      const activeJobs = jobs.filter(j => isActiveJobStatus(j.status)).length;
      const totalApplications = applications.length;
      const totalInterviewsConducted = interviews.filter(i => i.status === 'completed' || i.status === 'scored').length;
      const hiredCount = applications.filter(a => a.stage === 'hired').length;
      const hireRate = totalApplications > 0 ? Math.round((hiredCount / totalApplications) * 100) : 0;

      const totalCandidates = 0;
      const interviewed = applications.filter(a => ['interviewed', 'second_interview', 'offering', 'signing_contract', 'hired'].includes(a.stage)).length;
      const jobsWithApplications = new Set(applications.map((a) => a.job_id).filter(Boolean)).size;

      const activeJobIds = new Set(jobs.filter(j => isActiveJobStatus(j.status)).map(j => j.id));
      const activeJobApplications = applications.filter(a => activeJobIds.has(a.job_id));
      const totalActiveApplications = activeJobApplications.length;
      const activeJobsWithApps = new Set(activeJobApplications.map(a => a.job_id));
      const zeroApplicationJobs = jobs.filter(j => isActiveJobStatus(j.status) && !activeJobsWithApps.has(j.id)).length;
      const now = new Date();
      const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const closingSoon = jobs.filter(j => isActiveJobStatus(j.status) && j.closes_at && new Date(j.closes_at) <= sevenDaysFromNow && new Date(j.closes_at) > now).length;

      // Month-over-month trends
      const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = thisMonthStart;

      const inRange = (dateStr: string | null, start: Date, end: Date) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        return d >= start && d < end;
      };

      const calcTrend = (thisVal: number, lastVal: number) => {
        if (lastVal === 0) return thisVal > 0 ? 100 : 0;
        return Math.round(((thisVal - lastVal) / lastVal) * 100);
      };

      const jobsThisMonth = jobs.filter(j => isPublishedJobStatus(j.status) && inRange(j.created_at, thisMonthStart, now)).length;
      const jobsLastMonth = jobs.filter(j => isPublishedJobStatus(j.status) && inRange(j.created_at, lastMonthStart, lastMonthEnd)).length;

      const appsThisMonth = applications.filter(a => inRange(a.created_at, thisMonthStart, now)).length;
      const appsLastMonth = applications.filter(a => inRange(a.created_at, lastMonthStart, lastMonthEnd)).length;

      const interviewsThisMonth = interviews.filter(i => (i.status === 'completed' || i.status === 'scored') && inRange(i.completed_at, thisMonthStart, now)).length;
      const interviewsLastMonth = interviews.filter(i => (i.status === 'completed' || i.status === 'scored') && inRange(i.completed_at, lastMonthStart, lastMonthEnd)).length;

      const hiredThisMonth = applications.filter(a => a.stage === 'hired' && inRange(a.created_at, thisMonthStart, now)).length;
      const hiredLastMonth = applications.filter(a => a.stage === 'hired' && inRange(a.created_at, lastMonthStart, lastMonthEnd)).length;
      const hireRateThisMonth = appsThisMonth > 0 ? Math.round((hiredThisMonth / appsThisMonth) * 100) : 0;
      const hireRateLastMonth = appsLastMonth > 0 ? Math.round((hiredLastMonth / appsLastMonth) * 100) : 0;

      const jobsTrend = calcTrend(jobsThisMonth, jobsLastMonth);
      const applicationsTrend = calcTrend(appsThisMonth, appsLastMonth);
      const interviewsTrend = calcTrend(interviewsThisMonth, interviewsLastMonth);
      const hireRateTrend = calcTrend(hireRateThisMonth, hireRateLastMonth);

      // Average days to hire
      const hiredApps = applications.filter(a => a.stage === 'hired');
      let avgDaysToHire = 0;
      if (hiredApps.length > 0) {
        const totalDays = hiredApps.reduce((sum, a) => {
          const created = new Date(a.created_at);
          const updated = new Date(a.updated_at);
          return sum + Math.max(1, Math.round((updated.getTime() - created.getTime()) / (1000 * 60 * 60 * 24)));
        }, 0);
        avgDaysToHire = Math.round((totalDays / hiredApps.length) * 10) / 10;
      }

      return { activeJobs, totalJobsCreated, totalCandidates, totalApplications, totalInterviewsConducted, hireRate, interviewed, jobsWithApplications, totalActiveApplications, zeroApplicationJobs, closingSoon, jobsTrend, applicationsTrend, interviewsTrend, hireRateTrend, avgDaysToHire, activeInterviewConfigs };
    },
    enabled: !!orgId,
  });
}

export function useRecentCandidates() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['dashboard', 'recent-candidates', orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('applications')
        .select(`
          *,
          cv_scores (*),
          candidates:candidate_id (name, email, source),
          jobs:job_id (title, status)
        `)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      // Filter out unscored applied-stage and non-active job applications, then take top 5
      return (data || [])
        .filter((a) => {
          if (HIDDEN_CV_SCORING_STATUSES.has(a.status)) return false;
          if (a.stage === 'applied' && a.cv_scores?.total_score == null) return false;
          if (a.jobs?.status !== 'active') return false;
          return true;
        })
        .slice(0, 5);
    },
    enabled: !!orgId,
  });
}

export function useUsageMeters() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;
  const monthKey = new Date().toISOString().slice(0, 7);

  return useQuery({
    queryKey: ['dashboard', 'usage', orgId, monthKey],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('usage_meters')
        .select('*')
        .eq('org_id', orgId!)
        .eq('month_key', monthKey)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });
}

export function useTopPerformingJobs() {
  const { profile } = useAuth();
  const orgId = profile?.org_id;

  return useQuery({
    queryKey: ['dashboard', 'top-jobs', orgId],
    queryFn: async () => {
      // Get active jobs with their application counts
      const { data: jobs, error: jobsError } = await supabase
        .from('jobs')
        .select('id, title, department, location')
        .eq('org_id', orgId!)
        .eq('status', 'active')
        .limit(20);

      if (jobsError) throw jobsError;
      if (!jobs?.length) return [];

      // Get application counts per job
      const { data: apps, error: appsError } = await supabase
        .from('applications')
        .select('job_id');

      if (appsError) throw appsError;

      const countMap = new Map<string, number>();
      apps?.forEach(a => countMap.set(a.job_id, (countMap.get(a.job_id) || 0) + 1));

      return jobs
        .map(j => ({ ...j, applicationCount: countMap.get(j.id) || 0 }))
        .sort((a, b) => b.applicationCount - a.applicationCount)
        .slice(0, 5);
    },
    enabled: !!orgId,
  });
}
